import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import DropdownMenu from './DropdownMenu';
import ShuttlrBrandMark from './ShuttlrBrandMark';
import { useTheme } from '../../contexts/ThemeContext';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';

const AppHeader = ({
  currentUser,
  onLogout,
  showNotifications,
  setShowNotifications,
  unreadNotificationCount,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActiveHome = location.pathname === '/';
  const isActiveClubs =
    location.pathname === '/clubs' ||
    location.pathname.startsWith('/clubs/') ||
    location.pathname.startsWith('/club/');
  const isActiveSessions = location.pathname === '/upcoming-sessions';

  const activeClass = (active) => (active ? 'bg-white/15' : '');

  const navLinks = [
    { label: 'Home', to: '/', active: isActiveHome },
    { label: 'Clubs', to: '/clubs', active: isActiveClubs },
    { label: 'Sessions', to: '/upcoming-sessions', active: isActiveSessions },
  ];

  const moreItems = [
    { label: 'Middlesex League', action: () => navigate('/leagues/middlesex') },
    ...(currentUser?.role === 'admin' || currentUser?.role === 'super_admin'
      ? [{ label: 'League admin', action: () => navigate('/league-admin') }]
      : []),
    ...(currentUser?.role === 'super_admin'
      ? [{ label: 'Matchmaking Info', action: () => navigate('/matchmaking-info') }]
      : []),
    {
      label: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
      action: toggle,
    },
    { divider: true },
    { label: 'Logout', variant: 'danger', action: onLogout },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    document.body.style.overflow = 'hidden';

    const handleEscape = (e) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  const handleMobileNav = (action) => {
    closeMobileMenu();
    action();
  };

  return (
    <header className="sticky top-0 z-40 md:relative md:pt-6 md:pb-5">
      <div className="overflow-visible bg-gradient-to-r from-[#0a1628] via-slate-900 to-slate-800 text-white shadow-brand-lg md:rounded-2xl md:ring-1 md:ring-black/15 dark:md:ring-white/10">
        <div className="flex items-center gap-2 sm:gap-4 px-4 sm:px-6 lg:px-8 py-3 sm:py-4">

          {/* Brand */}
          <Link to="/" className="flex items-center space-x-2.5 shrink-0">
            <ShuttlrBrandMark />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-white leading-tight">Shuttlr</h1>
              <p className="hidden sm:block text-[0.6875rem] sm:text-xs text-slate-300 leading-tight">
                Play. Track. Improve.
              </p>
            </div>
          </Link>

          {/* Desktop nav — hidden on mobile */}
          <nav
            className="hidden md:flex flex-1 items-center justify-start min-w-0 overflow-visible"
            aria-label="Main navigation"
          >
            <div className={`flex items-center space-x-0.5 min-w-max ${interactiveBleedInsetX}`}>
              {navLinks.map(({ label, to, active }) => (
                <Button
                  key={to}
                  as={Link}
                  to={to}
                  variant="nav"
                  size="small"
                  className={`whitespace-nowrap ${activeClass(active)}`}
                >
                  {label}
                </Button>
              ))}

              <DropdownMenu
                trigger={
                  <Button
                    variant="nav"
                    size="small"
                    className="flex items-center space-x-1 whitespace-nowrap shrink-0"
                  >
                    <span>More</span>
                    <svg
                      className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                }
                items={moreItems}
                align="left"
              />
            </div>
          </nav>

          {/* Spacer on mobile pushes actions to the right */}
          <div className="flex-1 md:hidden" aria-hidden="true" />

          {/* Right: profile + notifications + menu (mobile) */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
            <Link
              to="/profile"
              className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center overflow-hidden shrink-0 hover:ring-2 hover:ring-white/30 transition-all focus:outline-none focus:ring-2 focus:ring-white/30"
              aria-label="View profile"
            >
              {currentUser.avatar_url ? (
                <img
                  src={getPlayerAvatarPublicUrl(currentUser.avatar_url)}
                  alt={`${currentUser.first_name} ${currentUser.last_name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold text-white select-none">
                  {currentUser.first_name?.[0]}{currentUser.last_name?.[0]}
                </span>
              )}
            </Link>

            <div className="hidden md:block text-sm leading-tight">
              <p className="font-medium text-white">
                {currentUser.first_name} {currentUser.last_name}
              </p>
              <Link
                to="/profile"
                className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
              >
                View profile
              </Link>
            </div>

            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
              aria-label="Notifications"
            >
              <svg
                className="w-4 h-4 text-slate-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-danger text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center pointer-events-none">
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="md:hidden w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="w-5 h-5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile slide-out drawer */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[100] md:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <nav
            className="fixed top-0 right-0 h-full w-72 max-w-[85vw] z-[100] md:hidden bg-gradient-to-b from-[#0a1628] via-slate-900 to-slate-800 text-white shadow-2xl flex flex-col"
            aria-label="Mobile navigation"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center space-x-2.5">
                <ShuttlrBrandMark />
                <div>
                  <p className="font-bold text-white leading-tight">Shuttlr</p>
                  <p className="text-xs text-slate-400 leading-tight">Play. Track. Improve.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Primary nav links */}
            <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {navLinks.map(({ label, to, active }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={closeMobileMenu}
                  className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    active
                      ? 'bg-white/15 text-white'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {label}
                </Link>
              ))}

              <div className="my-3 border-t border-white/10" />

              <button
                type="button"
                onClick={() => handleMobileNav(() => navigate('/leagues/middlesex'))}
                className="w-full flex items-center px-4 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-colors text-left"
              >
                Middlesex League
              </button>
              {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
                <button
                  type="button"
                  onClick={() => handleMobileNav(() => navigate('/league-admin'))}
                  className="w-full flex items-center px-4 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-colors text-left"
                >
                  League admin
                </button>
              )}
              {currentUser?.role === 'super_admin' && (
                <button
                  type="button"
                  onClick={() => handleMobileNav(() => navigate('/matchmaking-info'))}
                  className="w-full flex items-center px-4 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-colors text-left"
                >
                  Matchmaking Info
                </button>
              )}

              <button
                type="button"
                onClick={() => handleMobileNav(toggle)}
                className="w-full flex items-center px-4 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-colors text-left"
              >
                {isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              </button>

              <div className="my-3 border-t border-white/10" />

              <button
                type="button"
                onClick={() => handleMobileNav(onLogout)}
                className="w-full flex items-center px-4 py-3 rounded-lg text-base font-medium text-red-400 hover:bg-red-500/10 transition-colors text-left"
              >
                Logout
              </button>
            </div>

            {/* Drawer footer — user info */}
            <div className="px-5 py-4 border-t border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center overflow-hidden shrink-0">
                  {currentUser.avatar_url ? (
                    <img
                      src={getPlayerAvatarPublicUrl(currentUser.avatar_url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-white">
                      {currentUser.first_name?.[0]}{currentUser.last_name?.[0]}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-white truncate">
                    {currentUser.first_name} {currentUser.last_name}
                  </p>
                  <Link
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    View profile
                  </Link>
                </div>
              </div>
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default AppHeader;
